import debounce from 'lodash/debounce';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaSearch, FaTimes } from 'react-icons/fa';

interface SearchInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  debounceTime?: number;
}

export const SearchInput = ({
  value,
  onChange,
  placeholder,
  className = '',
  debounceTime = 300,
}: SearchInputProps) => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState(value);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const debouncedOnChange = useMemo(
    () => debounce((value: string) => onChange(value), debounceTime),
    [onChange, debounceTime],
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    debouncedOnChange(newValue);
  };

  const handleClear = () => {
    setInputValue('');
    onChange('');
  };

  return (
    <div className={`relative ${className}`}>
      <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
        <FaSearch className="text-gray-light" />
      </div>
      <input
        type="text"
        value={inputValue}
        onChange={handleChange}
        placeholder={placeholder || t('searchInput:placeholder')}
        className="block w-full rounded-lg border border-gray-light bg-transparent px-10 py-2 text-secondary focus:outline-none sm:text-sm"
      />
      {inputValue && (
        <button
          onClick={handleClear}
          className="absolute inset-y-0 right-0 flex items-center pr-3"
        >
          <FaTimes className="text-gray-light hover:text-gray-dark" />
        </button>
      )}
    </div>
  );
};
